import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserSessionService } from 'src/app/user-session.service';
import { BalanceSyncService } from './balance-sync.service';
import { BusinessAccountService } from '../business-account.service';
import { ClientOperationConfirmationUiRequest, ClientOperationConfirmationUiService } from './client-operation-confirmation-ui.service';

export interface BalanceSocketMessage {
  eventType?: string;
  username?: string;
  ownerType?: string;
  ownerId?: number;
  accountType?: string;
  role?: string;
  accountNumber?: string;
  balance?: number;
  accountBalance?: number;
  soldeOnline?: number;
  depotInit?: number;
  soldeBenefice?: number;
  bdepot?: number;
  bretrait?: number;
  sdepotPartenaire?: number;
  sretraitPartenaire?: number;
  currency?: string;
  occurredAt?: string;
  confirmationId?: number;
  operationType?: string;
  amount?: number;
  feeAmount?: number;
  taxAmount?: number;
  totalDebitAmount?: number;
  caissierUsername?: string;
  businessAccountId?: number;
  businessName?: string;
  primaryAccountNumber?: number;
  expiresAt?: string;
  status?: string;
  resultCode?: number;
  resultMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeBalanceService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectTimer: any = null;
  private readonly reconnectDelayMs = 3000;
  private readonly balanceMessageSubject = new Subject<BalanceSocketMessage>();

  readonly balanceMessages$ = this.balanceMessageSubject.asObservable();

  constructor(
    private userSessionService: UserSessionService,
    private balanceSyncService: BalanceSyncService,
    private businessAccountService: BusinessAccountService,
    private clientOperationConfirmationUiService: ClientOperationConfirmationUiService
  ) { }

  start(): void {
    const token = this.getToken();
    if (!token) {
      this.stop();
      return;
    }
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const wsUrl = this.buildSocketUrl(token);
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      this.clearReconnectTimer();
      this.loadPendingConfirmations();
    };

    this.socket.onmessage = (event: MessageEvent<string>) => {
      this.handleMessage(event.data);
    };

    this.socket.onclose = () => {
      this.socket = null;
      this.scheduleReconnect();
    };

    this.socket.onerror = () => {
      if (this.socket) {
        try {
          this.socket.close();
        } catch {
          // No-op
        }
      }
    };
  }

  stop(): void {
    this.clearReconnectTimer();
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // No-op
      }
      this.socket = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private handleMessage(raw: string): void {
    if (!raw) {
      return;
    }

    let message: BalanceSocketMessage | null = null;
    try {
      message = JSON.parse(raw) as BalanceSocketMessage;
    } catch {
      return;
    }

    if (!message?.eventType) {
      return;
    }

    if (message.eventType === 'BALANCE_CHANGED') {
      this.balanceMessageSubject.next(message);
      this.balanceSyncService.syncNow().pipe(take(1)).subscribe({
        error: () => {
          // No-op: la prochaine action utilisateur relancera une sync.
        }
      });
      return;
    }

    if (message.eventType === 'CLIENT_OPERATION_CONFIRMATION_REQUEST') {
      this.handleConfirmationRequest(message);
      return;
    }

    if (message.eventType === 'CLIENT_OPERATION_CONFIRMATION_RESULT') {
      const confirmationId = Number(message.confirmationId);
      if (Number.isFinite(confirmationId) && confirmationId > 0) {
        this.clientOperationConfirmationUiService.completeCurrent(confirmationId);
      }
      return;
    }
  }

  private handleConfirmationRequest(message: BalanceSocketMessage): void {
    const request = this.normalizeRequest(message);
    if (!request) {
      return;
    }
    this.clientOperationConfirmationUiService.enqueue(request);
  }

  private loadPendingConfirmations(): void {
    if (!this.shouldLoadPendingConfirmations()) {
      return;
    }

    this.businessAccountService.getPendingClientOperationConfirmations()
      .pipe(take(1))
      .subscribe({
        next: (items: any[] | null | undefined) => {
          if (!items || !Array.isArray(items)) {
            return;
          }
          for (const item of items) {
            const request = this.normalizeRequest(item as BalanceSocketMessage);
            if (request) {
              this.clientOperationConfirmationUiService.enqueue(request);
            }
          }
        },
        error: () => {
          // No-op
        }
      });
  }

  private shouldLoadPendingConfirmations(): boolean {
    const userRoles = this.userSessionService.getUser()?.roles ?? [];
    const normalizedRoles = userRoles
      .map(role => (role || '').toUpperCase().replace(/^ROLE_/, '').trim())
      .filter(role => role.length > 0);

    if (normalizedRoles.length === 0) {
      const legacyRole = (localStorage.getItem('inTheRoleOf') || '').toUpperCase().replace(/^ROLE_/, '').trim();
      if (legacyRole) {
        normalizedRoles.push(legacyRole);
      }
    }

    return normalizedRoles.includes('ADMIN')
      || normalizedRoles.includes('USER')
      || normalizedRoles.includes('VENDOR');
  }

  private scheduleReconnect(): void {
    const token = this.getToken();
    if (!token || this.reconnectTimer) {
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.start();
    }, this.reconnectDelayMs);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private getToken(): string | null {
    const token = this.userSessionService.getUser()?.token || localStorage.getItem('tokenConnectedUserWSU1234567GHTVB');
    return token ? token.replace(/^CBCL\s+/i, '').trim() : null;
  }

  private buildSocketUrl(token: string): string {
    const baseUrl = (environment.apiUrl || '').trim();
    const wsBase = baseUrl.startsWith('https://')
      ? baseUrl.replace(/^https:\/\//i, 'wss://')
      : baseUrl.replace(/^http:\/\//i, 'ws://');

    const normalizedBase = wsBase.endsWith('/') ? wsBase.slice(0, -1) : wsBase;
    return `${normalizedBase}/ws/balance?token=${encodeURIComponent(token)}`;
  }

  private normalizeRequest(message: BalanceSocketMessage): ClientOperationConfirmationUiRequest | null {
    const confirmationId = Number(message.confirmationId);
    const amount = Number(message.amount ?? 0);
    if (!Number.isFinite(confirmationId) || confirmationId <= 0) {
      return null;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }

    return {
      confirmationId,
      operationType: message.operationType || 'UNKNOWN',
      amount,
      feeAmount: Number.isFinite(Number(message.feeAmount)) ? Number(message.feeAmount) : undefined,
      taxAmount: Number.isFinite(Number(message.taxAmount)) ? Number(message.taxAmount) : undefined,
      totalDebitAmount: Number.isFinite(Number(message.totalDebitAmount)) ? Number(message.totalDebitAmount) : undefined,
      currency: (message.currency || 'FCFA').toUpperCase(),
      caissierUsername: message.caissierUsername || undefined,
      businessAccountId: Number.isFinite(Number(message.businessAccountId)) ? Number(message.businessAccountId) : undefined,
      businessName: message.businessName || undefined,
      primaryAccountNumber: Number.isFinite(Number(message.primaryAccountNumber)) ? Number(message.primaryAccountNumber) : undefined,
      expiresAt: message.expiresAt || undefined
    };
  }
}
